/**
 * Прогрев HTTP-кэша браузера картинками, которые позже рисует Fabric
 * (`fabric.Image.fromURL` берёт уже закэшированный ресурс мгновенно).
 *
 * Промис никогда не реджектится: битая или зависшая картинка не должна
 * блокировать показ оверлея, поэтому неудача — это просто `failed` в отчёте.
 */

/** Максимум ожидания одной картинки — защита от зависшего соединения. */
const IMAGE_TIMEOUT_MS = 10_000;
/** Общий бюджет на всю пачку — чтобы прогрев не держал экран загрузки бесконечно. */
const TOTAL_TIMEOUT_MS = 900_000;

export interface PreloadImagesOptions {
  imageTimeoutMs?: number;
  totalTimeoutMs?: number;
}

export interface PreloadImagesResult {
  total: number;
  loaded: number;
  failed: number;
  /** Пачка не уложилась в общий бюджет — часть картинок осталась догружаться в фоне. */
  isTimedOut: boolean;
}

function preloadImage(url: string, timeoutMs: number): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();

    // Таймер держит ссылку на img, пока идёт загрузка, — GC не оборвёт запрос.
    const timeoutId = setTimeout(() => settle(false), timeoutMs);

    function settle(isLoaded: boolean) {
      clearTimeout(timeoutId);
      img.onload = null;
      img.onerror = null;
      resolve(isLoaded);
    }

    img.onload = () => settle(true);
    img.onerror = () => settle(false);
    img.src = url;
  });
}

export async function preloadImages(
  urls: (string | undefined | null)[],
  options?: PreloadImagesOptions,
): Promise<PreloadImagesResult> {
  const { imageTimeoutMs = IMAGE_TIMEOUT_MS, totalTimeoutMs = TOTAL_TIMEOUT_MS } = options ?? {};

  const uniqueUrls = Array.from(new Set(urls.filter((url): url is string => !!url)));
  if (!uniqueUrls.length) return { total: 0, loaded: 0, failed: 0, isTimedOut: false };

  const { results, isTimedOut } = await Promise.race([
    Promise.all(uniqueUrls.map((url) => preloadImage(url, imageTimeoutMs))).then((loaded) => ({
      results: loaded,
      isTimedOut: false,
    })),
    new Promise<{ isTimedOut: boolean; results: boolean[] }>((resolve) =>
      setTimeout(() => resolve({ results: [], isTimedOut: true }), totalTimeoutMs),
    ),
  ]);

  const loaded = results.filter(Boolean).length;
  return {
    total: uniqueUrls.length,
    loaded,
    failed: results.length - loaded,
    isTimedOut,
  };
}
